import React, { useState, useEffect, useRef } from 'react';
import {
  API_BASE_URL, RESOURCES, ENUMS, SIDEBAR, METAS,
  envelope, errorEnvelope,
} from '../data/apiReference';
import './ApiReference.css';

// ── JSON syntax highlighter ──────────────────────────────────────────────────
function hl(str) {
  return String(str).replace(
    /("(?:\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(?:true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (m) => {
      if (/^"/.test(m)) return /:$/.test(m) ? `<span class="jk">${m}</span>` : `<span class="js">${m}</span>`;
      if (/true|false/.test(m)) return `<span class="jb">${m}</span>`;
      if (/null/.test(m)) return `<span class="jn">${m}</span>`;
      return `<span class="ji">${m}</span>`;
    }
  );
}

// SPA navigation triggered from inline markdown-style links inside descriptions.
// Mirrors the App's pushState + popstate pattern without needing the navigate
// callback to be threaded through.
function spaNavigate(href) {
  const url = href.startsWith('/') ? href : `/${href}`;
  window.history.pushState(null, '', url);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// Render inline `backtick`, **bold**, and [label](path) spans within
// plain-text descriptions. Links route through the SPA.
function renderInline(str) {
  const parts = String(str).split(/(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((p, i) => {
    if (/^`.*`$/.test(p)) return <code key={i} className="api-inline-code">{p.slice(1, -1)}</code>;
    if (/^\*\*.*\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
    const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(p);
    if (linkMatch) {
      const [, label, href] = linkMatch;
      const url = href.startsWith('/') ? href : `/${href}`;
      return (
        <a
          key={i}
          className="api-inline-link"
          href={url}
          onClick={(e) => {
            // Plain left-click without modifiers: SPA navigate. Otherwise
            // (cmd-click, middle-click, etc.) fall through to default.
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
            e.preventDefault();
            spaNavigate(href);
          }}
        >
          {label}
        </a>
      );
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

// Multi-paragraph text with inline code support.
function RichText({ children }) {
  const paragraphs = String(children).split(/\n\n+/);
  return (
    <>
      {paragraphs.map((p, i) => (
        <p key={i} className="api-prose">{renderInline(p)}</p>
      ))}
    </>
  );
}

// ── Primitives ───────────────────────────────────────────────────────────────

function JsonBlock({ value }) {
  const str = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return <pre className="api-code-pre" dangerouslySetInnerHTML={{ __html: hl(str) }} />;
}

function HttpBlock({ children }) {
  return <pre className="api-code-http">{children}</pre>;
}

function MethodBadge({ method, size = 'md' }) {
  return (
    <span className={`api-method api-method--${method.toLowerCase()} api-method--${size}`}>
      {method}
    </span>
  );
}

function TypePill({ type }) {
  return <code className="api-type">{type}</code>;
}

function InlineCode({ children }) {
  return <code className="api-inline-code">{children}</code>;
}

function FieldLabel({ children, action }) {
  return (
    <div className="api-field-label">
      <span className="api-field-label-text">{children}</span>
      <span className="api-field-label-line" aria-hidden="true" />
      {action}
    </div>
  );
}

// Strict filter: keep only top-level rows marked `required`. Children are kept
// untouched on rows that pass — required-only is checked at the outermost
// level of the request body, not recursively.
function filterRequired(rows) {
  return rows.filter((r) => r.required);
}

function hasOptionalAnywhere(rows) {
  return rows.some((r) => !r.required);
}

function AnchorLink({ targetId }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    const url = `${window.location.origin}${window.location.pathname}#${targetId}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* give up */ }
      document.body.removeChild(ta);
    }
    if (window.history.replaceState) {
      window.history.replaceState(null, '', `#${targetId}`);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  }

  return (
    <button
      type="button"
      className={`api-anchor-link${copied ? ' api-anchor-link--ok' : ''}`}
      onClick={copy}
      aria-label={copied ? 'Link copied' : 'Copy link to this section'}
      title={copied ? 'Copied' : 'Copy link'}
    >
      {copied ? (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
          <path d="M3 6.5L5.5 9 10 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ) : (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
          <path d="M5.4 7.6a2 2 0 002.83 0l2.12-2.12a2 2 0 10-2.83-2.83l-.7.7M7.6 5.4a2 2 0 00-2.83 0L2.65 7.52a2 2 0 102.83 2.83l.7-.7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </button>
  );
}

function Note({ variant = 'warning', children }) {
  return <div className={`api-note api-note--${variant}`}>{children}</div>;
}

// ── Copy-to-clipboard button ─────────────────────────────────────────────────

function CopyButton({ value, theme = 'dark', label = 'Copy' }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for non-secure contexts / older browsers
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* give up */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  }

  return (
    <button
      type="button"
      className={`api-copy api-copy--${theme}${copied ? ' api-copy--ok' : ''}`}
      onClick={copy}
      aria-label={copied ? 'Copied' : label}
      title={copied ? 'Copied' : label}
    >
      {copied ? (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M2.5 6.5L5 9l4.5-5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <rect x="3.5" y="3.5" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.2"/>
          <path d="M2 8V3a1 1 0 011-1h4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
        </svg>
      )}
    </button>
  );
}

// ── localStorage hook ────────────────────────────────────────────────────────

function useLocalStorage(key, initial) {
  const [v, setV] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw == null ? initial : JSON.parse(raw);
    } catch { return initial; }
  });
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* ignore */ }
  }, [key, v]);
  return [v, setV];
}

// ── Code panel (dark, with tabs) ─────────────────────────────────────────────

function CodePanel({ tabs, title, onTryIt }) {
  const [active, setActive] = useState(0);
  // Clamp if tabs array changes
  const tab = tabs[Math.min(active, tabs.length - 1)];
  const copyValue = tab.type === 'http' ? tab.code : tab.body ?? tab.code;
  return (
    <div className="api-code-panel">
      <div className="api-code-head">
        {title && <div className="api-code-title">{title}</div>}
        <div className="api-code-head-right">
          {tabs.length > 1 && (
            <div className="api-code-tabs">
              {tabs.map((t, i) => (
                <button
                  key={i}
                  className={`api-code-tab${active === i ? ' api-code-tab--active' : ''}`}
                  onClick={() => setActive(i)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
          <CopyButton value={copyValue} label={`Copy ${tab.label.toLowerCase()}`} />
          {onTryIt && (
            <button className="api-code-try" onClick={onTryIt}>
              Try it
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="M3 2l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>
      </div>
      {tab.type === 'http' ? <HttpBlock>{tab.code}</HttpBlock> : <JsonBlock value={tab.body ?? tab.code} />}
    </div>
  );
}

// ── Try-it panel (interactive request) ───────────────────────────────────────

function statusVariant(s) {
  if (s < 300) return 'ok';
  if (s < 500) return 'warn';
  return 'err';
}

function TryItPanel({ endpoint, onBack }) {
  const [apiKey, setApiKey] = useLocalStorage('valyx-api-key', '');
  const [baseUrl, setBaseUrl] = useLocalStorage('valyx-base-url', API_BASE_URL);
  const [showKey, setShowKey] = useState(false);
  const [body, setBody] = useState(() =>
    endpoint.requestExample ? JSON.stringify(endpoint.requestExample, null, 2) : ''
  );
  const [sending, setSending] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  const pathParams = Array.isArray(endpoint.pathParams) ? endpoint.pathParams : [];
  // Prefill path-param values from localStorage so users only enter once across endpoints
  // sharing the same param name.
  const [pathValues, setPathValues] = useState(() => {
    const values = {};
    for (const p of pathParams) {
      try {
        values[p.name] = localStorage.getItem(`valyx-pathparam-${p.name}`) || '';
      } catch { values[p.name] = ''; }
    }
    return values;
  });

  function setPathValue(name, value) {
    setPathValues((v) => ({ ...v, [name]: value }));
    try { localStorage.setItem(`valyx-pathparam-${name}`, value); } catch { /* ignore */ }
  }

  const resolvedPath = endpoint.path.replace(/\{(\w+)\}/g, (_, name) => {
    const v = (pathValues[name] || '').trim();
    return v ? encodeURIComponent(v) : `{${name}}`;
  });

  const missingPathParams = pathParams.filter((p) => !(pathValues[p.name] || '').trim());
  const pathParamMissing = missingPathParams.length > 0;

  const hasBody = Array.isArray(endpoint.requestBody) && endpoint.requestBody.length > 0;
  const requiresApiKey = endpoint.authNote !== 'session-only';
  const trimmedKey = apiKey.trim();
  const keyMissing = requiresApiKey && trimmedKey.length === 0;

  async function send() {
    setError(null);
    setResponse(null);

    if (keyMissing) {
      setError('An API key is required to call this endpoint.');
      return;
    }

    if (pathParamMissing) {
      setError(`Provide a value for: ${missingPathParams.map((p) => p.name).join(', ')}.`);
      return;
    }

    setSending(true);

    let parsedBody;
    if (hasBody && body.trim()) {
      try {
        parsedBody = JSON.parse(body);
      } catch (e) {
        setError(`Invalid JSON in request body: ${e.message}`);
        setSending(false);
        return;
      }
    }

    const url = `${baseUrl.replace(/\/$/, '')}${resolvedPath}`;
    const key = trimmedKey;
    const start = performance.now();

    try {
      const res = await fetch(url, {
        method: endpoint.method,
        headers: {
          'Content-Type': 'application/json',
          ...(key ? { 'X-Valyx-Signature': key } : {}),
        },
        body: parsedBody !== undefined ? JSON.stringify(parsedBody) : undefined,
        credentials: 'include',
      });

      const duration = Math.round(performance.now() - start);
      const contentType = res.headers.get('content-type') ?? '';
      const text = await res.text();
      let payload = text;
      if (contentType.includes('application/json') && text) {
        try { payload = JSON.parse(text); } catch { /* keep as text */ }
      }

      setResponse({ status: res.status, body: payload, duration });
    } catch (e) {
      const msg = e?.message ?? String(e);
      const looksLikeCors = /failed to fetch|networkerror|cors/i.test(msg);
      setError(
        looksLikeCors
          ? `Network error — the browser could not reach ${url}. This is usually a CORS restriction (the API must allow requests from ${window.location.origin}) or an unreachable base URL.`
          : msg
      );
    } finally {
      setSending(false);
    }
  }

  const lineCount = Math.min(16, Math.max(6, body.split('\n').length + 1));
  const respStr = response && (typeof response.body === 'string'
    ? response.body
    : JSON.stringify(response.body, null, 2));

  return (
    <div className="api-try">
      <div className="api-try-head">
        <div className="api-try-head-title">
          <span className={`api-method api-method--${endpoint.method.toLowerCase()} api-method--md`}>{endpoint.method}</span>
          <code className="api-try-path">{resolvedPath}</code>
        </div>
        <button className="api-try-back" onClick={onBack}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path d="M7 2L4 5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Examples
        </button>
      </div>

      <div className="api-try-body">
        <div className="api-try-section">
          <div className="api-try-section-label">Authentication</div>
          <div className="api-try-field">
            <label htmlFor={`${endpoint.id}-key`}>
              X-Valyx-Signature
              {requiresApiKey && (
                <span className="api-try-required" aria-hidden="true"> *</span>
              )}
            </label>
            <div className="api-try-input-wrap">
              <input
                id={`${endpoint.id}-key`}
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Paste your API key…"
                spellCheck={false}
                autoComplete="off"
                required={requiresApiKey}
                aria-required={requiresApiKey}
                aria-invalid={keyMissing}
              />
              <button
                type="button"
                className="api-try-input-btn"
                onClick={() => setShowKey((s) => !s)}
                tabIndex={-1}
              >
                {showKey ? 'hide' : 'show'}
              </button>
            </div>
            {requiresApiKey && (
              <div className="api-try-field-hint">
                Required. Mint a key at <code>/app/apiKey/generate</code>.
              </div>
            )}
          </div>
          <div className="api-try-field">
            <label htmlFor={`${endpoint.id}-url`}>Base URL</label>
            <input
              id={`${endpoint.id}-url`}
              type="text"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              spellCheck={false}
              autoComplete="off"
            />
          </div>
          {endpoint.authNote === 'session-only' && (
            <div className="api-try-hint">
              This endpoint requires a session cookie, not an API key. You'll need to be
              logged in to <code>{baseUrl}</code> for the request to succeed.
            </div>
          )}
        </div>

        {pathParams.length > 0 && (
          <div className="api-try-section">
            <div className="api-try-section-label">Path parameters</div>
            {pathParams.map((p) => {
              const value = pathValues[p.name] || '';
              const isMissing = !value.trim();
              return (
                <div className="api-try-field" key={p.name}>
                  <label htmlFor={`${endpoint.id}-path-${p.name}`}>
                    {p.name}
                    <span className="api-try-required" aria-hidden="true"> *</span>
                  </label>
                  <input
                    id={`${endpoint.id}-path-${p.name}`}
                    type="text"
                    value={value}
                    onChange={(e) => setPathValue(p.name, e.target.value)}
                    placeholder={`Enter ${p.name}…`}
                    spellCheck={false}
                    autoComplete="off"
                    required
                    aria-required={true}
                    aria-invalid={isMissing}
                  />
                  {p.description && (
                    <div className="api-try-field-hint">{p.description}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {hasBody && (
          <div className="api-try-section">
            <div className="api-try-section-head">
              <div className="api-try-section-label">Request body</div>
              <CopyButton value={body} label="Copy request body" />
            </div>
            <textarea
              className="api-try-textarea"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={lineCount}
              spellCheck={false}
              autoComplete="off"
            />
          </div>
        )}

        <button
          className="api-try-send"
          onClick={send}
          disabled={sending || keyMissing || pathParamMissing}
          title={
            keyMissing
              ? 'Enter an API key to enable Send'
              : pathParamMissing
                ? `Provide ${missingPathParams.map((p) => p.name).join(', ')} to enable Send`
                : undefined
          }
        >
          {sending ? (
            <>
              <span className="api-try-spinner" aria-hidden="true" />
              Sending…
            </>
          ) : (
            <>Send request</>
          )}
        </button>

        {error && (
          <div className="api-try-alert api-try-alert--err">
            <div className="api-try-alert-title">Request failed</div>
            <div className="api-try-alert-body">{error}</div>
          </div>
        )}

        {response && (
          <div className="api-try-response">
            <div className="api-try-response-head">
              <span className={`api-status api-status--${statusVariant(response.status)}`}>
                {response.status}
              </span>
              <span className="api-try-duration">{response.duration} ms</span>
              <CopyButton value={respStr ?? ''} label="Copy response" />
            </div>
            <pre
              className="api-code-pre api-try-response-body"
              dangerouslySetInnerHTML={{ __html: hl(respStr ?? '') }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tables ────────────────────────────────────────────────────────────────────

function FieldsTable({ rows, showRequired = false }) {
  // Insert group headers wherever the `group` property changes between
  // adjacent rows. Order is preserved as authored in the data file.
  const items = [];
  let currentGroup = null;
  for (const r of rows) {
    if (r.group && r.group !== currentGroup) {
      currentGroup = r.group;
      items.push({ kind: 'header', label: r.group });
    } else if (!r.group && currentGroup !== null) {
      // a row without a group after grouped rows — reset so a later grouped
      // row will emit a header again, but emit no header for this run.
      currentGroup = null;
    }
    items.push({ kind: 'row', row: r });
  }

  return (
    <div className="api-fields">
      {items.map((it, i) =>
        it.kind === 'header' ? (
          <div key={`h-${i}`} className="api-fields-group">{it.label}</div>
        ) : (
          <FieldRow key={it.row.name} row={it.row} showRequired={showRequired} />
        )
      )}
    </div>
  );
}

function FieldRow({ row, showRequired }) {
  const hasChildren = Array.isArray(row.children) && row.children.length > 0;
  const [expanded, setExpanded] = useState(false);
  const childCount = hasChildren ? row.children.length : 0;

  // When a row has children, its description is hidden on first render — only
  // the name + type + required pill + "Show N attributes" toggle are visible,
  // which keeps long endpoint pages scannable. Description appears alongside
  // children on expand. Rows without children always show their description.
  const showDescription = row.description && (!hasChildren || expanded);

  return (
    <div className="api-field-row">
      <div className="api-field-row-head">
        <code className="api-param-name">{row.name}</code>
        <TypePill type={row.type} />
        {showRequired && (
          <span className={`api-req api-req--${row.required ? 'yes' : 'no'}`}>
            {row.required ? 'required' : 'optional'}
          </span>
        )}
      </div>
      {showDescription && (
        <div className="api-field-row-desc">{renderInline(row.description)}</div>
      )}
      {hasChildren && (
        <button
          type="button"
          className={`api-field-toggle${expanded ? ' api-field-toggle--open' : ''}`}
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <svg
            className="api-field-toggle-chev"
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            aria-hidden="true"
          >
            <path d="M2.5 3.5L5 6l2.5-2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {expanded
            ? 'Hide child attributes'
            : `Show ${childCount} child ${childCount === 1 ? 'attribute' : 'attributes'}`}
        </button>
      )}
      {hasChildren && expanded && (
        <div className="api-field-children">
          {row.children.map((c) => (
            <FieldRow key={c.name} row={c} showRequired={showRequired} />
          ))}
        </div>
      )}
    </div>
  );
}

function ErrorTable({ rows }) {
  return (
    <table className="api-table">
      <thead>
        <tr><th>Status</th><th>When</th></tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td>
              <code className={`api-status api-status--${r.status >= 500 ? 'err' : 'warn'}`}>{r.status}</code>
            </td>
            <td className="api-td-desc">{renderInline(r.when)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Layout ────────────────────────────────────────────────────────────────────

function TwoCol({ left, right }) {
  return (
    <div className="api-two-col">
      <div className="api-col-doc">{left}</div>
      <div className="api-col-code">
        <div className="api-col-code-inner">{right}</div>
      </div>
    </div>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

function resolveItem(item) {
  if (item.kind === 'resource') {
    const res = RESOURCES[item.id];
    return {
      id: item.id,
      kind: 'resource',
      label: res.label,
      endpoints: res.endpoints,
      children: Array.isArray(item.children) ? item.children.map(resolveItem) : undefined,
    };
  }
  return { id: item.id, kind: 'meta', label: METAS[item.id]?.label ?? item.id };
}

function matchesFilter(item, q) {
  if (!q) return true;
  const needle = q.toLowerCase();
  if (item.label.toLowerCase().includes(needle)) return true;
  if (item.kind === 'resource') {
    if (item.endpoints?.some((ep) =>
      ep.path.toLowerCase().includes(needle) ||
      ep.title?.toLowerCase().includes(needle) ||
      ep.method.toLowerCase().includes(needle)
    )) return true;
    if (item.children?.some((c) => matchesFilter(c, q))) return true;
  }
  return false;
}


function SidebarItemRow({ item, active, onNavigate, q, depth }) {
  const isActive = active === item.id;
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;

  // A child of this item is active — auto-expand to reveal it.
  const childIsActive = hasChildren && item.children.some((c) => isInChain(c, active));

  const showEndpoints = (isActive || q) && item.kind === 'resource' && item.endpoints?.length > 0;
  const visibleChildren = hasChildren
    ? (q ? item.children.filter((c) => matchesFilter(c, q)) : item.children)
    : [];
  const showChildren = hasChildren
    && visibleChildren.length > 0
    && (isActive || childIsActive || q);

  const matchedEndpoints = q && item.kind === 'resource'
    ? item.endpoints.filter((ep) =>
        ep.path.toLowerCase().includes(q.toLowerCase()) ||
        ep.title?.toLowerCase().includes(q.toLowerCase()) ||
        ep.method.toLowerCase().includes(q.toLowerCase()) ||
        item.label.toLowerCase().includes(q.toLowerCase())
      )
    : item.endpoints;

  return (
    <div className={depth > 0 ? 'api-sidebar-subitem' : undefined}>
      <button
        className={`api-sidebar-item${isActive ? ' api-sidebar-item--active' : ''}${depth > 0 ? ' api-sidebar-item--nested' : ''}`}
        onClick={() => onNavigate(`api-reference/${item.id}`)}
      >
        <span className="api-sidebar-item-label">{item.label}</span>
      </button>

      {showEndpoints && matchedEndpoints?.length > 0 && (
        <div className={`api-sidebar-endpoints${depth > 0 ? ' api-sidebar-endpoints--nested' : ''}`}>
          {matchedEndpoints.map((ep) => (
            <a
              key={ep.id}
              href={`#${ep.id}`}
              className="api-sidebar-endpoint"
              onClick={(e) => {
                e.preventDefault();
                if (active !== item.id) {
                  onNavigate(`api-reference/${item.id}`);
                  setTimeout(() => {
                    document.getElementById(ep.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 80);
                } else {
                  document.getElementById(ep.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
            >
              <span className={`api-sidebar-method api-sidebar-method--${ep.method.toLowerCase()}`}>
                {ep.method}
              </span>
              <span className="api-sidebar-endpoint-path">{ep.path}</span>
            </a>
          ))}
        </div>
      )}

      {showChildren && (
        <div className="api-sidebar-children">
          {visibleChildren.map((c) => (
            <SidebarItemRow
              key={c.id}
              item={c}
              active={active}
              onNavigate={onNavigate}
              q={q}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Helper: does `item` (or any of its descendants) match `activeId`?
function isInChain(item, activeId) {
  if (item.id === activeId) return true;
  if (item.children) return item.children.some((c) => isInChain(c, activeId));
  return false;
}

function ApiSidebar({ active, onNavigate }) {
  const [filter, setFilter] = useState('');
  const q = filter.trim();

  // Compute filtered groups (skip empty ones)
  const filteredGroups = SIDEBAR
    .map((group) => ({
      label: group.label,
      items: group.items
        .map(resolveItem)
        .filter((item) => matchesFilter(item, q)),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <nav className="api-sidebar">
      <div className="api-sidebar-head">
        <div className="api-sidebar-title">API Reference</div>
        <div className="api-sidebar-filter">
          <svg className="api-sidebar-filter-icon" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <circle cx="5" cy="5" r="3.25" stroke="currentColor" strokeWidth="1.4"/>
            <path d="M7.5 7.5L10 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            className="api-sidebar-filter-input"
            placeholder="Filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            spellCheck={false}
            autoComplete="off"
            aria-label="Filter API reference"
          />
          {filter && (
            <button
              type="button"
              className="api-sidebar-filter-clear"
              onClick={() => setFilter('')}
              aria-label="Clear filter"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="api-sidebar-scroll">
        {filteredGroups.length === 0 && (
          <div className="api-sidebar-empty">No matches for "{q}"</div>
        )}

        {filteredGroups.map((group) => (
          <div key={group.label} className="api-sidebar-group">
            <div className="api-sidebar-group-label">{group.label}</div>
            {group.items.map((item) => (
              <SidebarItemRow
                key={item.id}
                item={item}
                active={active}
                onNavigate={onNavigate}
                q={q}
                depth={0}
              />
            ))}
          </div>
        ))}
      </div>
    </nav>
  );
}

// ── Endpoint block ───────────────────────────────────────────────────────────

function EndpointBlock({ endpoint }) {
  const [tryMode, setTryMode] = useState(false);
  const [requiredOnly, setRequiredOnly] = useState(false);

  const requestBody = endpoint.requestBody;
  const showRequiredToggle = Array.isArray(requestBody)
    && requestBody.length > 0
    && hasOptionalAnywhere(requestBody);
  const visibleRequestBody = (requiredOnly && requestBody)
    ? filterRequired(requestBody)
    : requestBody;

  const codeTabs = [];
  if (endpoint.requestExample) {
    codeTabs.push({ label: 'Request', body: endpoint.requestExample });
  }
  for (const ex of endpoint.responseExamples ?? []) {
    codeTabs.push({ label: ex.label, body: ex.body });
  }
  for (const extra of endpoint.extras ?? []) {
    codeTabs.push({ label: extra.label, type: extra.type, code: extra.code });
  }

  return (
    <section id={endpoint.id} className="api-endpoint">
      <TwoCol
        left={
          <>
            <div className="api-section-title-wrap">
              <h3 className="api-endpoint-title">{endpoint.title}</h3>
              <AnchorLink targetId={endpoint.id} />
            </div>
            <div className="api-endpoint-sig">
              <MethodBadge method={endpoint.method} />
              <code className="api-path">{endpoint.path}</code>
              <CopyButton
                value={`${API_BASE_URL}${endpoint.path}`}
                theme="light"
                label="Copy full URL"
              />
            </div>
            <div className="api-endpoint-desc">
              <RichText>{endpoint.description}</RichText>
            </div>

            {endpoint.authNote === 'session-only' && (
              <Note variant="warning">
                <strong>Session only.</strong> This endpoint does not accept an API key —
                you must have an active session cookie. This prevents key holders from
                minting further keys programmatically.
              </Note>
            )}

            {endpoint.pathParams && (
              <>
                <FieldLabel>Path parameters</FieldLabel>
                <FieldsTable rows={endpoint.pathParams} />
              </>
            )}

            {endpoint.queryParams && (
              <>
                <FieldLabel>Query parameters</FieldLabel>
                <FieldsTable rows={endpoint.queryParams} showRequired />
              </>
            )}

            {endpoint.requestBody && (
              <>
                <FieldLabel
                  action={showRequiredToggle ? (
                    <button
                      type="button"
                      className="api-required-toggle"
                      onClick={() => setRequiredOnly((v) => !v)}
                      aria-pressed={requiredOnly}
                      title={requiredOnly ? 'Show all fields' : 'Show only required fields'}
                    >
                      {requiredOnly ? 'Showing required only' : 'Required only'}
                    </button>
                  ) : null}
                >
                  Request body
                </FieldLabel>
                {visibleRequestBody.length === 0 ? (
                  <div className="api-fields-empty">
                    No required fields — every field on this endpoint is optional.
                  </div>
                ) : (
                  <FieldsTable rows={visibleRequestBody} showRequired />
                )}
              </>
            )}

            {endpoint.responseFields && (
              <>
                <FieldLabel>Response fields</FieldLabel>
                <FieldsTable rows={endpoint.responseFields} />
              </>
            )}

            {endpoint.errors && endpoint.errors.length > 0 && (
              <>
                <FieldLabel>Errors</FieldLabel>
                <ErrorTable rows={endpoint.errors} />
              </>
            )}
          </>
        }
        right={
          tryMode ? (
            <TryItPanel endpoint={endpoint} onBack={() => setTryMode(false)} />
          ) : (
            <CodePanel
              title={`${endpoint.method} ${endpoint.path}`}
              tabs={codeTabs}
              onTryIt={() => setTryMode(true)}
            />
          )
        }
      />
    </section>
  );
}

// ── Resource page ────────────────────────────────────────────────────────────

function scrollToAnchor(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  // Update the hash without jumping (smooth scroll already handled).
  if (window.history.replaceState) {
    window.history.replaceState(null, '', `#${id}`);
  }
}

function EndpointNavCard({ endpoints, hasObject }) {
  return (
    <aside className="api-endpoint-nav" aria-label="Endpoints on this page">
      <ul className="api-endpoint-nav-list">
        {hasObject && (
          <li>
            <a
              href="#object"
              className="api-endpoint-nav-item"
              onClick={(e) => { e.preventDefault(); scrollToAnchor('object'); }}
            >
              <span className="api-endpoint-nav-item-main">
                <span className="api-endpoint-nav-item-title">The object</span>
                <span className="api-endpoint-nav-item-sig api-endpoint-nav-item-sig--muted">
                  Schema &amp; example
                </span>
              </span>
              <svg className="api-endpoint-nav-chev" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M5.5 3.5L9 7l-3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </li>
        )}
        {endpoints.map((ep) => (
          <li key={ep.id}>
            <a
              href={`#${ep.id}`}
              className="api-endpoint-nav-item"
              onClick={(e) => { e.preventDefault(); scrollToAnchor(ep.id); }}
            >
              <span className="api-endpoint-nav-item-main">
                <span className="api-endpoint-nav-item-title">{ep.title}</span>
                <span className="api-endpoint-nav-item-sig">
                  <MethodBadge method={ep.method} size="sm" />
                  <code className="api-endpoint-nav-path">{ep.path}</code>
                </span>
              </span>
              <svg className="api-endpoint-nav-chev" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M5.5 3.5L9 7l-3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </li>
        ))}
      </ul>
    </aside>
  );
}

function ResourcePage({ resource }) {
  const endpoints = resource.endpoints ?? [];
  const hasNav = endpoints.length > 0 || !!resource.object;

  return (
    <>
      <header className={`api-page-header${hasNav ? ' api-page-header--split' : ''}`}>
        <div className="api-page-header-main">
          <div className="api-page-eyebrow">Resource</div>
          <h1 className="api-page-title">{resource.label}</h1>
          {resource.tagline && <div className="api-page-tagline">{resource.tagline}</div>}
          {resource.description && (
            <div className="api-page-description">
              <RichText>{resource.description}</RichText>
            </div>
          )}
        </div>
        {hasNav && (
          <EndpointNavCard endpoints={endpoints} hasObject={!!resource.object} />
        )}
      </header>

      {resource.object && (
        <section id="object" className="api-object-section">
          <TwoCol
            left={
              <>
                <div className="api-section-title-wrap">
                  <h2 className="api-section-title">{resource.object.name}</h2>
                  <AnchorLink targetId="object" />
                </div>
                <FieldsTable rows={resource.object.fields} />
              </>
            }
            right={
              <CodePanel
                title="Example object"
                tabs={[{ label: 'JSON', body: resource.object.example }]}
              />
            }
          />
        </section>
      )}

      {(resource.endpoints ?? []).map((ep) => (
        <EndpointBlock key={ep.id} endpoint={ep} />
      ))}
    </>
  );
}

// ── Meta pages ───────────────────────────────────────────────────────────────

function IntroductionPage() {
  return (
    <>
      <header className="api-page-header">
        <div className="api-page-eyebrow">API Reference</div>
        <h1 className="api-page-title">Valyx API</h1>
        <div className="api-page-tagline">
          Programmatic access to your tax rates, catalog, contracts, and usage data.
        </div>
      </header>

      <section className="api-plain-section">
        <TwoCol
          left={
            <>
              <h2 className="api-section-title">Base URL</h2>
              <p className="api-prose">
                All endpoints are served from a single base URL. Each path in this
                reference should be appended to it.
              </p>
              <div className="api-kv">
                <span className="api-kv-k">Production</span>
                <code className="api-kv-v">{API_BASE_URL}</code>
              </div>
            </>
          }
          right={
            <CodePanel
              title="Base URL"
              tabs={[{ label: 'Shell', type: 'http', code: `curl ${API_BASE_URL}/app/tax/list \\\n  -H "X-Valyx-Signature: $VALYX_KEY"` }]}
            />
          }
        />
      </section>

      <section className="api-plain-section">
        <TwoCol
          left={
            <>
              <h2 className="api-section-title">What's in this reference</h2>
              <p className="api-prose">
                Endpoints are organized by resource. Each resource page documents the
                object shape and the endpoints that act on it. Every request and
                response uses the same <InlineCode>&#123; error, message, data &#125;</InlineCode> envelope.
              </p>
              <p className="api-prose">
                All <InlineCode>app/</InlineCode> endpoints require authentication
                via either a session cookie or an API key. See <em>Authentication</em>
                for details.
              </p>
            </>
          }
          right={
            <CodePanel
              title="Response envelope"
              tabs={[
                { label: 'Success', body: envelope('Operation succeeded', { /* ... */ }) },
                { label: 'Error', body: errorEnvelope('ValidationError', 'Invalid input') },
              ]}
            />
          }
        />
      </section>
    </>
  );
}

function AuthenticationPage() {
  return (
    <>
      <header className="api-page-header">
        <div className="api-page-eyebrow">Getting started</div>
        <h1 className="api-page-title">Authentication</h1>
        <div className="api-page-tagline">Session cookies and API keys</div>
      </header>

      <section className="api-plain-section">
        <TwoCol
          left={
            <>
              <h2 className="api-section-title">Two ways to authenticate</h2>
              <p className="api-prose">
                All <InlineCode>app/</InlineCode> endpoints accept credentials from
                one of two sources. Both resolve to the same <InlineCode>userId</InlineCode>{' '}
                and <InlineCode>companyId</InlineCode> — handlers behave identically
                regardless of which method was used.
              </p>
              <table className="api-table api-table--auth">
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>How to send</th>
                    <th>When to use</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Session cookie</strong></td>
                    <td><code className="api-param-name">token</code> cookie (set after login)</td>
                    <td>Browser / frontend</td>
                  </tr>
                  <tr>
                    <td><strong>API key</strong></td>
                    <td><code className="api-param-name">X-Valyx-Signature</code> header</td>
                    <td>Programmatic / server-to-server</td>
                  </tr>
                </tbody>
              </table>
              <Note variant="warning">
                <strong>Minting keys:</strong> Only a session cookie can call{' '}
                <InlineCode>/app/apiKey/generate</InlineCode>. API key holders cannot
                mint further keys. Keys generated with <InlineCode>expiresInDays</InlineCode>{' '}
                return <InlineCode>403</InlineCode> after their expiry.
              </Note>
            </>
          }
          right={
            <CodePanel
              title="Unauthenticated response"
              tabs={[
                { label: '403 Forbidden', body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a session token or an API key.') },
              ]}
            />
          }
        />
      </section>
    </>
  );
}

function ResponseFormatPage() {
  return (
    <>
      <header className="api-page-header">
        <div className="api-page-eyebrow">Getting started</div>
        <h1 className="api-page-title">Response format</h1>
        <div className="api-page-tagline">One envelope for every response</div>
      </header>

      <section className="api-plain-section">
        <TwoCol
          left={
            <>
              <h2 className="api-section-title">Standard envelope</h2>
              <p className="api-prose">
                Every endpoint — success or failure — returns the same top-level
                shape. The HTTP status code tells you whether the request succeeded;
                the envelope fields tell you what happened.
              </p>
              <FieldsTable rows={[
                { name: 'error', type: 'string | null', description: '`null` on success; an error type identifier on failure.' },
                { name: 'message', type: 'string', description: 'Human-readable description of the result.' },
                { name: 'data', type: 'object | array | null', description: 'Response payload on success. `null` on failure.' },
              ]} />
            </>
          }
          right={
            <CodePanel
              title="Envelope shape"
              tabs={[
                { label: 'Success', body: envelope('Operation succeeded', { /* payload */ }) },
                { label: 'Error', body: errorEnvelope('ValidationError', 'Description of what failed') },
              ]}
            />
          }
        />
      </section>
    </>
  );
}

function ErrorsPage() {
  return (
    <>
      <header className="api-page-header">
        <div className="api-page-eyebrow">Getting started</div>
        <h1 className="api-page-title">Errors</h1>
        <div className="api-page-tagline">Status codes and error types</div>
      </header>

      <section className="api-plain-section">
        <TwoCol
          left={
            <>
              <h2 className="api-section-title">HTTP status codes</h2>
              <p className="api-prose">
                The API uses conventional HTTP status codes to signal the outcome of
                a request. Every non-2xx response still includes the standard
                envelope with an <InlineCode>error</InlineCode> identifier and a
                human-readable <InlineCode>message</InlineCode>.
              </p>
              <table className="api-table">
                <thead>
                  <tr><th>Code</th><th>Meaning</th></tr>
                </thead>
                <tbody>
                  <tr><td><code className="api-status api-status--ok">200</code></td><td>Request succeeded.</td></tr>
                  <tr><td><code className="api-status api-status--ok">201</code></td><td>Resource created.</td></tr>
                  <tr><td><code className="api-status api-status--warn">400</code></td><td>Invalid input. Check the <InlineCode>message</InlineCode> for the specific field.</td></tr>
                  <tr><td><code className="api-status api-status--warn">403</code></td><td>Missing, invalid, or expired credentials.</td></tr>
                  <tr><td><code className="api-status api-status--warn">404</code></td><td>The target resource does not exist.</td></tr>
                  <tr><td><code className="api-status api-status--err">500</code></td><td>Server error. Retry with backoff.</td></tr>
                </tbody>
              </table>
            </>
          }
          right={
            <CodePanel
              title="Error examples"
              tabs={[
                { label: '400', body: errorEnvelope('ValidationError', 'Tax rate cannot be negative') },
                { label: '403', body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a session token or an API key.') },
                { label: '500', body: errorEnvelope('InternalError', 'An unexpected error occurred. Please retry.') },
              ]}
            />
          }
        />
      </section>
    </>
  );
}

function EnumsPage() {
  return (
    <>
      <header className="api-page-header">
        <div className="api-page-eyebrow">Reference</div>
        <h1 className="api-page-title">Enum types</h1>
        <div className="api-page-tagline">Accepted values for enum-typed fields</div>
      </header>

      <section className="api-enum-grid">
        {Object.values(ENUMS).map((en) => (
          <div key={en.id} id={en.id} className="api-enum-card">
            <div className="api-enum-card-head">
              <code className="api-enum-card-name">{en.id}</code>
              {en.description && (
                <div className="api-enum-card-desc">{renderInline(en.description)}</div>
              )}
            </div>
            <table className="api-enum-table">
              <tbody>
                {en.values.map((v) => (
                  <tr key={v.value}>
                    <td><code className="api-param-name">{v.value}</code></td>
                    <td>{v.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </section>
    </>
  );
}

function TaxModellingPage() {
  const intraStateExample = {
    gstTax: {
      cgst: 9000.0,
      sgst: 9000.0,
    },
  };

  const interStateExample = {
    gstTax: {
      igst: 18000.0,
    },
  };

  const gstPlusCessExample = {
    gstTax: {
      cgst: 9000.0,
      sgst: 9000.0,
    },
    cessTax: { cess: 12000.0 },
  };

  const reverseChargeExample = {
    reverseChargeTax: {
      reverseChargeTax: 18000.0,
    },
  };

  const otherTaxesExample = {
    otherTaxes: [
      { name: 'VAT (UK)', genericTax: 12500.0 },
    ],
  };

  return (
    <>
      <header className="api-page-header">
        <div className="api-page-eyebrow">Reference</div>
        <h1 className="api-page-title">Tax modelling</h1>
        <div className="api-page-tagline">
          How to populate the <InlineCode>Tax</InlineCode> object on invoices and line items.
        </div>
        <div className="api-page-description">
          <RichText>
            {`The \`Tax\` object is intentionally rich because Indian invoices need to represent several independent tax regimes — GST, cess, reverse charge, plus legacy service tax and arbitrary other levies — on the same line. This page is a reference for how the pieces fit together.`}
          </RichText>
        </div>
      </header>

      <section className="api-plain-section">
        <TwoCol
          left={
            <>
              <h2 className="api-section-title">Anatomy</h2>
              <p className="api-prose">
                A <InlineCode>Tax</InlineCode> block is a flat aggregate (<InlineCode>total</InlineCode>) plus up to five <strong>independent</strong> component blocks. Every component is optional — send only the ones that apply; the rest are stored as <InlineCode>null</InlineCode> (or <InlineCode>[]</InlineCode> for <InlineCode>otherTaxes</InlineCode>) and round-trip the same way.
              </p>
              <FieldsTable rows={[
                { name: 'total', type: 'number', description: 'Aggregate tax amount, in the invoice currency.' },
                { name: 'gstTax', type: 'GstTax | null', description: 'GST regime breakdown.' },
                { name: 'cessTax', type: 'CessTax | null', description: 'Cess (often paired with GST).' },
                { name: 'serviceTax', type: 'ServiceTax | null', description: 'Legacy pre-GST service tax.' },
                { name: 'reverseChargeTax', type: 'ReverseChargeTax | null', description: 'Buyer-paid taxes (reverse charge).' },
                { name: 'otherTaxes', type: 'array<OtherTax>', description: 'Named, free-form taxes (VAT, local levies, etc.).' },
              ]} />
            </>
          }
          right={
            <CodePanel
              title="Tax shape"
              tabs={[{ label: 'Skeleton', body: {
                total: 0,
                gstTax: null,
                cessTax: null,
                serviceTax: null,
                reverseChargeTax: null,
                otherTaxes: [],
              } }]}
            />
          }
        />
      </section>

      <section className="api-plain-section">
        <TwoCol
          left={
            <>
              <h2 className="api-section-title">Send amounts only — rates are derived</h2>
              <p className="api-prose">
                You only send <strong>amounts</strong> (currency values) on requests. Valyx auto-calculates the corresponding <strong>rates</strong> (percentages) against the relevant subtotal and returns them in the response. Sending rate fields on a request is a no-op.
              </p>
              <FieldsTable rows={[
                { name: 'gstTax', type: 'amounts', description: 'Send `totalGst`, `cgst`, `sgst`, `igst`. Response also contains `totalGstRate`, `cgstRate`, `sgstRate`, `igstRate`.' },
                { name: 'cessTax', type: 'amount', description: 'Send `cess`. Response also contains `cessRate`.' },
                { name: 'serviceTax', type: 'amount', description: 'Send `serviceTax`. Response also contains `serviceTaxRate`.' },
                { name: 'reverseChargeTax', type: 'amount', description: 'Send `reverseChargeTax`. Response also contains `reverseChargeTaxRate`.' },
                { name: 'otherTaxes[i]', type: 'amount', description: 'Send `name` and `genericTax`. Response also contains `genericTaxRate`.' },
              ]} />
            </>
          }
          right={null}
        />
      </section>

      <section className="api-plain-section">
        <TwoCol
          left={
            <>
              <h2 className="api-section-title">Totals are derived</h2>
              <p className="api-prose">
                You don't need to send aggregate totals. Valyx computes them from the constituent amounts:
              </p>
              <FieldsTable rows={[
                { name: 'Tax.total', type: 'derived', description: 'Sum of `gstTax.totalGst`, `cessTax.cess`, `serviceTax.serviceTax`, `reverseChargeTax.reverseChargeTax`, and every `otherTaxes[i].genericTax`.' },
                { name: 'gstTax.totalGst', type: 'derived', description: '`cgst + sgst` for intra-state, `igst` for inter-state.' },
              ]} />
              <p className="api-prose">
                Both fields are accepted on input as an <strong>optional sanity check</strong>. If you supply a value that doesn't match what Valyx derives, the request is rejected with a <InlineCode>400</InlineCode>. Most callers should leave them out and let Valyx fill them in.
              </p>
            </>
          }
          right={null}
        />
      </section>

      <section className="api-plain-section">
        <TwoCol
          left={
            <>
              <h2 className="api-section-title">GST: intra-state vs. inter-state</h2>
              <p className="api-prose">
                GST is split based on whether the supply is intra-state (seller and buyer in the same state) or inter-state. Send <strong>either</strong> the CGST + SGST pair <strong>or</strong> IGST — never both.
              </p>
              <FieldsTable rows={[
                { name: 'Intra-state', type: 'CGST + SGST', description: 'Populate `cgst` and `sgst`. They must be **equal**. Leave `igst` unset (or zero).' },
                { name: 'Inter-state', type: 'IGST', description: 'Populate `igst` only. Leave `cgst` and `sgst` unset (or zero).' },
              ]} />
              <p className="api-prose">
                These constraints are enforced for non-draft invoices: both CGST and SGST set means IGST must be zero; IGST set means CGST and SGST must be zero; CGST and SGST must be equal when both are set. <InlineCode>totalGst</InlineCode> is auto-computed from whichever pair you send.
              </p>
            </>
          }
          right={
            <CodePanel
              title="GST 18% — intra-state"
              tabs={[{ label: 'Intra-state', body: intraStateExample }, { label: 'Inter-state', body: interStateExample }]}
            />
          }
        />
      </section>

      <section className="api-plain-section">
        <TwoCol
          left={
            <>
              <h2 className="api-section-title">Where each tax sits</h2>
              <p className="api-prose">
                <InlineCode>Tax</InlineCode> blocks appear in two places per invoice:
              </p>
              <FieldsTable rows={[
                { name: 'lineItems[i].tax', type: 'line-level', description: 'Tax owed on a specific line item. This is where most callers should put the actual numbers.' },
                { name: 'invoiceTax', type: 'invoice-level', description: 'Aggregate of all line taxes for the invoice as a whole.' },
              ]} />
              <p className="api-prose">
                Valyx aggregates line-item taxes and validates the result against <InlineCode>invoiceTax.total</InlineCode>. Mismatched aggregates produce a <InlineCode>400</InlineCode>.
              </p>
            </>
          }
          right={null}
        />
      </section>

      <section className="api-plain-section">
        <TwoCol
          left={
            <>
              <h2 className="api-section-title">Reverse charge, service tax, and other taxes</h2>
              <p className="api-prose">
                <strong>Reverse charge.</strong> <InlineCode>reverseChargeTax</InlineCode> is for transactions where the <strong>buyer</strong> is liable to pay tax to the government instead of the seller (common in B2B services under GST). It does not stack with the GST split — treat it as a separate component. The seller's GST blocks should reflect the original tax amount; reverse-charge is what shifts the liability.
              </p>
              <p className="api-prose">
                <strong>Service tax.</strong> <InlineCode>serviceTax</InlineCode> is the legacy pre-GST regime. Most current callers should leave this <InlineCode>null</InlineCode>. It's kept on the model for invoice imports and archived records.
              </p>
              <p className="api-prose">
                <strong>Other taxes.</strong> <InlineCode>otherTaxes</InlineCode> is a list of named entries — use it when none of the typed blocks fit (state-level surcharges, foreign-jurisdiction VAT, etc.). <InlineCode>name</InlineCode> is required and surfaces back in the response unchanged.
              </p>
            </>
          }
          right={
            <CodePanel
              title="otherTaxes example"
              tabs={[{ label: 'JSON', body: {
                otherTaxes: [
                  { name: 'VAT', genericTax: 1500.0 },
                  { name: 'Local levy', genericTax: 200.0 },
                ],
              } }]}
            />
          }
        />
      </section>

      <section className="api-plain-section">
        <TwoCol
          left={
            <>
              <h2 className="api-section-title">Empty and zero-valued blocks</h2>
              <p className="api-prose">
                If you send a component block with a zero amount (e.g. <InlineCode>cessTax: &#123; cess: 0 &#125;</InlineCode>), the response may collapse it back to <InlineCode>null</InlineCode>. Valyx doesn't refuse zero-valued blocks, but they round-trip as absent. Send <InlineCode>null</InlineCode> (or omit the field) when a component doesn't apply.
              </p>
            </>
          }
          right={null}
        />
      </section>

      <section className="api-plain-section">
        <TwoCol
          left={
            <>
              <h2 className="api-section-title">Worked examples</h2>
              <p className="api-prose">
                Five common shapes you'll encounter. Tap the tabs on the right to switch.
              </p>
              <FieldsTable rows={[
                { name: '1. Intra-state GST 18%', type: 'KA → KA', description: 'CGST + SGST split, each 9%. IGST is zero.' },
                { name: '2. Inter-state GST 18%', type: 'KA → MH', description: 'IGST only, at the full 18%. CGST and SGST are zero.' },
                { name: '3. GST 18% + Cess 12%', type: 'GST + cess', description: 'Both `gstTax` and `cessTax` populated. `total` is their sum.' },
                { name: '4. Reverse-charge B2B services', type: 'reverse charge', description: '`total` is zero — seller isn\'t collecting tax. Buyer remits to the government.' },
                { name: '5. Foreign VAT', type: 'otherTaxes', description: 'GST blocks empty; named entry under `otherTaxes`.' },
              ]} />
            </>
          }
          right={
            <CodePanel
              title="Worked examples"
              tabs={[
                { label: '1. Intra-state', body: intraStateExample },
                { label: '2. Inter-state', body: interStateExample },
                { label: '3. GST + cess', body: gstPlusCessExample },
                { label: '4. Reverse charge', body: reverseChargeExample },
                { label: '5. Foreign VAT', body: otherTaxesExample },
              ]}
            />
          }
        />
      </section>
    </>
  );
}

function AddressesPage() {
  const addressFields = [
    { name: 'line1', type: 'string', description: 'First line of the street address.' },
    { name: 'line2', type: 'string', description: 'Second line of the street address (apartment, suite, building, etc.).' },
    { name: 'city', type: 'string', description: 'City or town.' },
    { name: 'state', type: 'string', description: 'State or province name.' },
    { name: 'stateCode', type: 'string', description: 'Two-digit GST state code (for Indian addresses).' },
    { name: 'country', type: 'string', description: 'Country name. Auto-populated from `countryCode` on resources that have one.' },
    { name: 'zipCode', type: 'string', description: 'Postal or ZIP code.' },
    { name: 'gstin', type: 'string', description: 'Address-level GSTIN, if it differs from any resource-level tax identifier.' },
  ];

  const addressWithPhoneFields = [
    ...addressFields,
    { name: 'primaryPhone', type: 'string', description: 'Primary phone number for this address.' },
    { name: 'pan', type: 'string', description: 'Address-level PAN. **Response only** — auto-derived from `gstin`. Not accepted on input.' },
  ];

  const addressExample = {
    line1: '221B Baker Street',
    line2: null,
    city: 'Bengaluru',
    state: 'Karnataka',
    stateCode: '29',
    country: 'India',
    zipCode: '560001',
    gstin: null,
  };

  const addressWithPhoneExample = {
    line1: 'Seller Plaza',
    line2: null,
    city: 'Bengaluru',
    state: null,
    stateCode: '29',
    country: null,
    zipCode: null,
    gstin: '29AABCU9603R1Z2',
    pan: 'AABCU9603R',
    primaryPhone: '+919999999999',
  };

  return (
    <>
      <header className="api-page-header">
        <div className="api-page-eyebrow">Reference</div>
        <h1 className="api-page-title">Addresses</h1>
        <div className="api-page-tagline">
          Two address shapes are used across the API.
        </div>
        <div className="api-page-description">
          <RichText>
            {`Addresses appear on customers, invoices, and parties (seller / buyer snapshots). Two slightly different shapes are used: a base \`Address\` for resources that don't carry per-address contact info, and \`AddressWithPhone\` (a superset) for ones that do. Both share the same first eight fields, so you can usually treat them interchangeably for the common fields.`}
          </RichText>
        </div>
      </header>

      <section className="api-plain-section">
        <TwoCol
          left={
            <>
              <h2 className="api-section-title">Address</h2>
              <p className="api-prose">
                Used by <InlineCode>customer.registeredAddress</InlineCode>. Plain
                postal address with optional GSTIN.
              </p>
              <FieldsTable rows={addressFields} />
            </>
          }
          right={
            <CodePanel title="Address example" tabs={[{ label: 'JSON', body: addressExample }]} />
          }
        />
      </section>

      <section className="api-plain-section">
        <TwoCol
          left={
            <>
              <h2 className="api-section-title">AddressWithPhone</h2>
              <p className="api-prose">
                Used by <InlineCode>invoice.billingAddress</InlineCode>,{' '}
                <InlineCode>invoice.shippingAddress</InlineCode>,{' '}
                <InlineCode>sellerDetails.address</InlineCode>, and{' '}
                <InlineCode>buyerDetails.address</InlineCode>. Adds <InlineCode>primaryPhone</InlineCode>
                {' '}and a response-only <InlineCode>pan</InlineCode> derived from <InlineCode>gstin</InlineCode>.
              </p>
              <FieldsTable rows={addressWithPhoneFields} />
            </>
          }
          right={
            <CodePanel title="AddressWithPhone example" tabs={[{ label: 'JSON', body: addressWithPhoneExample }]} />
          }
        />
      </section>

      <section className="api-plain-section">
        <TwoCol
          left={
            <>
              <h2 className="api-section-title">PAN derivation</h2>
              <p className="api-prose">
                When you supply <InlineCode>gstin</InlineCode> on an{' '}
                <InlineCode>AddressWithPhone</InlineCode>, Valyx extracts the embedded PAN
                (positions 3–12 of the GSTIN) and returns it as <InlineCode>pan</InlineCode> in
                the response. Don't send <InlineCode>pan</InlineCode> on the request — it's
                ignored.
              </p>
              <p className="api-prose">
                For example, GSTIN <InlineCode>29AABCU9603R1Z2</InlineCode> yields PAN{' '}
                <InlineCode>AABCU9603R</InlineCode>.
              </p>
            </>
          }
          right={null}
        />
      </section>
    </>
  );
}

function NotFoundPage({ id, onNavigate }) {
  return (
    <div className="api-notfound">
      <div className="api-notfound-eyebrow">404</div>
      <h1 className="api-notfound-title">No such API section</h1>
      <p className="api-notfound-desc">
        <code className="api-inline-code">{id}</code> is not a valid API reference route.
      </p>
      <button className="api-notfound-btn" onClick={() => onNavigate('api-reference/introduction')}>
        ← Back to API Reference
      </button>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────

const META_PAGES = {
  introduction: IntroductionPage,
  authentication: AuthenticationPage,
  'response-format': ResponseFormatPage,
  errors: ErrorsPage,
  'tax-modelling': TaxModellingPage,
  addresses: AddressesPage,
  enums: EnumsPage,
};

export default function ApiReference({ subpage, onNavigate }) {
  const active = subpage || 'introduction';
  const contentRef = useRef(null);
  const resource = RESOURCES[active];
  const MetaPage = META_PAGES[active];

  // Scroll to top of content on sub-page change
  useEffect(() => {
    contentRef.current?.scrollTo?.({ top: 0 });
  }, [active]);

  return (
    <div className="api-ref">
      <ApiSidebar active={active} onNavigate={onNavigate} />
      <div className="api-ref-content" ref={contentRef}>
        {resource && <ResourcePage resource={resource} />}
        {!resource && MetaPage && <MetaPage />}
        {!resource && !MetaPage && <NotFoundPage id={active} onNavigate={onNavigate} />}
      </div>
    </div>
  );
}
